import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataContentProps, useUiAction } from '../../newron-core';
import { Instructions } from '../../content/instructions';
import { Loading } from '../../design/loading/Loading';
import { KpiTrendChart, TrendsInfo } from '../../design/charts';
import { makeTrendInfo } from './data';
import { ClickableCard } from '../../design/clickable.card/card';
import { TrendClickAction } from './types';
import { getExportContainer } from '../../export';
import { useOnCollectionExportReady } from '../collection/useOnCollectionExportReady';
import { useCorePageState } from '../../page/provider';
import { EmptyState } from '../../common/no-data-warning/empty-state';

function SingleKpiTrend({
  data,
  isLoading,
  instructions = {},
  componentSettings,
  componentName,
  collection,
}: DataContentProps) {
  const [dataStamp, setDataStamp] = useState<number>();
  const [chartInfo, setChartInfo] = useState<{ info: TrendsInfo; title?: string }>();
  const { perform } = useUiAction();
  const { dsl } = useCorePageState();
  const exportRef = useRef<HTMLDivElement[]>([]);
  const sharedBrands = dsl?.settings?.values?.sharedBrands || [];

  useOnCollectionExportReady({ collection, dsl, chartSliceRefs: exportRef, componentName });

  const myInstructions = new Instructions(instructions, componentSettings);

  const title = myInstructions.getString('title');
  const asCard = myInstructions.getBoolean('asCard', false);
  const grouping = myInstructions.getString('grouping');
  const kpi = myInstructions.getString('kpi');
  const secondaryKpi = myInstructions.getString('secondaryKpi');
  const includeTotals = myInstructions.getBoolean('includeTotals', false);
  const totalName = myInstructions.getString('totalName');
  const clickAction = myInstructions.getObject('clickAction') as TrendClickAction;
  const isAllowedToDisplayEmptyState = myInstructions.getBoolean('isAllowedToDisplayEmptyState', false);

  useEffect(() => {
    if (!data || dataStamp === data.stamp) {
      return;
    }
    const chartInfo = makeTrendInfo(data, { grouping, kpi, secondaryKpi, includeTotals, totalName, title });
    setChartInfo(chartInfo);
    setDataStamp(data.stamp);
  }, [data, grouping, kpi, secondaryKpi, includeTotals, totalName, title, dataStamp]);

  useEffect(() => {
    if (data) {
      collection?.onLoaded();
    }
  }, [data]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ExportContainer = useMemo(() => getExportContainer(componentName, 'inline'), []);

  const handleClick = () => {
    perform(clickAction.action, {
      kpi: [kpi],
    });
  };

  if (collection && data && !data.hasDataResultSetsValues() && !data.hasDataInSomeGroupedSets()) {
    collection.onExportReady(null);
    return isAllowedToDisplayEmptyState ? <EmptyState /> : <></>;
  }

  return (
    <ClickableCard
      outerCss="single-trend-card"
      innerCss={`${isLoading ? 'h-52' : ''}`}
      asCard={asCard}
      onClick={clickAction && !isLoading ? handleClick : undefined}
      linkText={clickAction?.label}
    >
      <Loading isActive={isLoading} withIndicator />
      {chartInfo?.title && <div className="pb-1">{chartInfo.title}</div>}
      {chartInfo && <KpiTrendChart info={chartInfo.info} sharedBrands={sharedBrands} />}
      {chartInfo && (
        <ExportContainer>
          <div
            className={`${componentName}-export-slide border border-light p-1 rounded text-xs w-2/6`}
            ref={el => (exportRef.current = [el])}
          >
            {chartInfo?.title && <div className="pb-1">{chartInfo.title}</div>}
            {<KpiTrendChart info={chartInfo.info} sharedBrands={sharedBrands} />}
          </div>
        </ExportContainer>
      )}
    </ClickableCard>
  );
}

export { SingleKpiTrend };
